// ─── City Store ─────────────────────────────────────────────────────────
// Single source of truth for all place data at runtime.
//
// On app init, loads all places from the unified `cities` table into memory.
// During the session, HAFAS-discovered stations are registered here.
// Persistence to Supabase happens server-side in the API endpoints.
//
// All components should use getCityById(), searchCities(), and resolveCity()
// from this module — never query place data directly.

import type { Database } from "../data/database.types";
import type { City } from "../data/types";
import { supabase } from "./supabaseClient";
import { extractCityName } from "./geoUtils";

// ─── In-memory store ────────────────────────────────────────────────────
const store = new Map<string, City>();
/** Maps lowercase alias strings → canonical City for local-language search */
const aliasIndex = new Map<string, City>();
let initialized = false;
let initialization: Promise<void> | null = null;

// ─── Initialization ─────────────────────────────────────────────────────

/**
 * Load all places from the unified `cities` table into the in-memory store.
 * Call once on app init (from App.tsx). Safe to call multiple times.
 *
 * Rows with a `hafas_station_id` are stored under BOTH keys so that
 * `getCityById("berlin")` and `getCityById("8011160")` both work.
 */
async function loadTable<T extends "cities" | "city_aliases">(table: T) {
    const rows: Database["public"]["Tables"][T]["Row"][] = [];
    for (let offset = 0; ; offset += 500) {
        const { data, error } = await supabase.from(table).select("*")
            .order(table === "cities" ? "city_key" : "alias").range(offset, offset + 499);
        if (error) throw error;
        rows.push(...data as unknown as Database["public"]["Tables"][T]["Row"][]);
        if (data.length < 500) return rows;
    }
}

function cityFromRow(row: Database["public"]["Tables"]["cities"]["Row"]): City | null {
    if (row.latitude == null || row.longitude == null) return null;
    return {
        id: row.city_key, name: row.canonical_name, city: row.canonical_name,
        country: row.country || "", population: row.population || 0,
        latitude: row.latitude, longitude: row.longitude,
        poiType: row.poi_type, poiName: row.poi_name, poiDescription: row.poi_description,
        imageUrl: row.image_url, poiShowcaseText: row.showcase_text,
        spotifyUri: row.spotify_uri, description: row.description,
        hafasId: row.hafas_station_id ?? undefined,
        attractions: Array.isArray(row.attractions) ? row.attractions.map(v => typeof v === "string" ? v : v.name) : undefined,
        foodSpecialties: Array.isArray(row.food_specialties) ? row.food_specialties.map(v => typeof v === "string" ? v : v.name) : undefined,
    };
}

export function initCityStore(): Promise<void> {
    if (initialized) return Promise.resolve();
    if (initialization) return initialization;
    initialization = (async () => {
        const [cities, aliases] = await Promise.all([loadTable("cities"), loadTable("city_aliases")]);
        // Commit only when both reads succeed. Failed boots can be retried.
        for (const row of cities) {
            const city = cityFromRow(row);
            if (!city) continue;
            store.set(city.id, city);
            if (city.hafasId) store.set(city.hafasId, city);
        }
        for (const row of aliases) {
            const city = store.get(row.city_key);
            if (!city) continue;
            const lower = row.alias.toLowerCase();
            aliasIndex.set(lower, city);
            aliasIndex.set(lower.normalize("NFD").replaceAll(/[\u0300-\u036f]/g, ""), city);
        }
        initialized = true;
    })().finally(() => { initialization = null; });
    return initialization;
}

// ─── Registration ───────────────────────────────────────────────────────

/**
 * Register a city in the store. If already exists, merges
 * (new non-empty values take precedence, never overwrites
 * rich data with null/undefined).
 */
function registerCity(city: City): void {
    const existing = store.get(city.id);
    store.set(city.id, {
        ...existing,
        ...city,
        city: city.city || existing?.city || "",
        country: city.country || existing?.country || "",
        latitude: city.latitude || existing?.latitude || 0,
        longitude: city.longitude || existing?.longitude || 0,
        attractions: city.attractions?.length ? city.attractions : existing?.attractions,
        foodSpecialties: city.foodSpecialties?.length ? city.foodSpecialties : existing?.foodSpecialties,
        population: city.population || existing?.population || 0,
        imageUrl: city.imageUrl || existing?.imageUrl || null,
        description: city.description || existing?.description || null,
        spotifyUri: city.spotifyUri || existing?.spotifyUri || null,
        poiType: city.poiType || existing?.poiType || null,
        poiName: city.poiName || existing?.poiName || null,
        poiDescription: city.poiDescription || existing?.poiDescription || null,
        poiShowcaseText: city.poiShowcaseText || existing?.poiShowcaseText || null,
        hafasId: city.hafasId || existing?.hafasId,
    });
}

/**
 * Register multiple cities at once (e.g. from route stopovers).
 */
export function registerCities(cities: City[] | Map<string, City>): void {
    cities.forEach((c) => registerCity(c));
}

// ─── City Builder (fire-and-forget) ─────────────────────────────────────

/** Slugs already requested this session — avoids duplicate POSTs. */
const pendingBuilds = new Set<string>();

/**
 * Fire-and-forget: ask the server to create & enrich a city that isn't
 * in the store yet. The city will appear on the next initCityStore() reload.
 */
export function requestCityBuild(
    slug: string, name: string,
    lat?: number, lon?: number, hafasId?: string,
): void {
    if (!hafasId || !/^\d{5,12}$/.test(hafasId) || pendingBuilds.has(slug)) return;
    pendingBuilds.add(slug);

    fetch('/api/cityBuilder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug, name, lat, lon, hafasId }),
    }).then(response => { if (!response.ok) pendingBuilds.delete(slug); }).catch(() => { pendingBuilds.delete(slug); });
}

// ─── Client-side Wikipedia enrichment ───────────────────────────────────

const WIKI_API = 'https://en.wikipedia.org/w/api.php';
const WIKI_REST = 'https://en.wikipedia.org/api/rest_v1/page/summary';
const IMG_REJECT = ['/flag_of_', 'coat_of_arms', '_flag.svg', '_flag.png',
    '_in_', 'wappen_von', 'escudo_de', 'blason_de'];

function imageFromWikiPages(pages: Record<string, { missing?: unknown; title?: string; thumbnail?: { source?: string } }> | undefined): string | null {
    if (!pages) return null;
    const page = pages[Object.keys(pages)[0]];
    if (!page || page.missing !== undefined || page.title?.toLowerCase().includes('(disambiguation)')) return null;
    const url = page.thumbnail?.source;
    if (url && IMG_REJECT.some(part => url.toLowerCase().includes(part))) return null;
    return url || null;
}

async function fetchWikiImage(cityName: string): Promise<string | null> {
    for (const term of [cityName, `${cityName} (city)`]) {
        try {
            const params = new URLSearchParams({
                action: 'query', prop: 'pageimages', format: 'json',
                piprop: 'thumbnail', pithumbsize: '400',
                titles: term, origin: '*', redirects: '1',
            });
            const res = await fetch(`${WIKI_API}?${params}`);
            if (!res.ok) continue;
            const data = await res.json();
            const url = imageFromWikiPages(data?.query?.pages);
            if (url) return url;
        } catch { /* next term */ }
    }
    return null;
}

function summarizeWikiExtract(extract: string): string | null {
    const sentences = extract.replaceAll(/\s+/g, ' ')
        .split(/(?<=[.!?])\s+(?=[A-Z])/)
        .filter(sentence => sentence.length > 10);
    if (sentences.length === 0) return null;
    let description = sentences.slice(0, 3).join(' ');
    if (description.length > 320) description = sentences.slice(0, 2).join(' ');
    if (description.length > 320) description = sentences[0];
    if (description.length > 320) description = description.slice(0, 317) + '...';
    return description.trim();
}

async function fetchWikiDescription(cityName: string): Promise<string | null> {
    for (const term of [cityName, `${cityName} (city)`]) {
        try {
            const res = await fetch(
                `${WIKI_REST}/${encodeURIComponent(term)}`,
                { headers: { 'Api-User-Agent': 'ViaFera/1.0 (https://viafera.eu)' } },
            );
            if (!res.ok) continue;
            const data = await res.json();
            if (data.type === 'disambiguation') continue;
            if (data.extract && data.extract.length > 30) {
                // Trim to 3 sentences / 320 chars (same logic as server)
                const description = summarizeWikiExtract(data.extract);
                if (description) return description;
            }
        } catch { /* next term */ }
    }
    return null;
}

/** IDs already enriched this session — avoids duplicate Wikipedia calls. */
const enrichedIds = new Set<string>();

/**
 * Client-side Wikipedia enrichment for newly discovered route cities.
 * Fires after registerCities() — fetches image + description for cities
 * missing that data, then merges results back into the store.
 * Fire-and-forget: does not block route display.
 */
export function enrichNewCities(cities: Map<string, City>): void {
    for (const [id, city] of cities) {
        if (enrichedIds.has(id)) continue;
        if (city.imageUrl && city.description) continue;
        const cityName = city.city;
        if (!cityName) continue;
        enrichedIds.add(id);

        // Fire-and-forget per city
        Promise.all([
            city.imageUrl ? Promise.resolve(null) : fetchWikiImage(cityName),
            city.description ? Promise.resolve(null) : fetchWikiDescription(cityName),
        ]).then(([imageUrl, description]) => {
            if (!imageUrl && !description) return;
            registerCity({
                ...city,
                imageUrl: imageUrl || city.imageUrl || null,
                description: description || city.description || null,
            });
        }).catch(() => { enrichedIds.delete(id); });
    }
}

// ─── Lookups ────────────────────────────────────────────────────────────

/**
 * Look up a city by ID (city_key, HAFAS EVA ID, or alias).
 * Checks the unified in-memory store first, then the alias index
 * so that local-language names (e.g. "wien" → vienna) resolve too.
 * ASCII-folds the key so diacritical variants also match.
 */
export function getCityById(id: string): City | undefined {
    const direct = store.get(id);
    if (direct) return direct;
    const lower = id.toLowerCase();
    const folded = lower.normalize("NFD").replaceAll(/[\u0300-\u036f]/g, "");
    return aliasIndex.get(lower) ?? aliasIndex.get(folded);
}

/**
 * Resolve an array of station IDs into lat/lng polyline coordinates.
 * Skips IDs that aren't in the store or lack coordinates.
 */
export function resolveRouteCoords(stationIds: string[]): [number, number][] {
    const coords: [number, number][] = [];
    for (const sid of stationIds) {
        const s = store.get(sid);
        if (s?.latitude && s.longitude) {
            coords.push([s.latitude, s.longitude]);
        }
    }
    return coords;
}

// ─── Local Search ───────────────────────────────────────────────────────

/**
 * Search the in-memory store for stations matching a query.
 * Matches on city, station name, and country (case-insensitive).
 * Results ranked: prefix matches first, then substring matches.
 * Deduplicates nearby stations (same city, ~15km), keeping the entry
 * with richer metadata (country, population).
 */
export function searchCities(query: string): City[] {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];

    const prefixMatches: City[] = [];
    const substringMatches: City[] = [];
    const seen = new Set<string>(); // track by city id to avoid dups from alias+store

    // 1. Search canonical entries in the store
    for (const entry of store.values()) {
        if (seen.has(entry.id)) continue;
        const city = entry.city.toLowerCase();
        const name = entry.name.toLowerCase();
        const country = entry.country.toLowerCase();

        if (city.startsWith(q) || name.startsWith(q)) {
            prefixMatches.push(entry);
            seen.add(entry.id);
        } else if (
            city.includes(q) ||
            name.includes(q) ||
            country.startsWith(q)
        ) {
            substringMatches.push(entry);
            seen.add(entry.id);
        }
    }

    // 2. Search alias index (local-language names: "milano", "göteborg", etc.)
    for (const [alias, entry] of aliasIndex) {
        if (seen.has(entry.id)) continue;
        if (alias.startsWith(q)) {
            const aliasLabel = alias.replaceAll(/(^|\s)\w/g, c => c.toUpperCase());
            prefixMatches.push({ ...entry, matchedAlias: aliasLabel });
            seen.add(entry.id);
        } else if (alias.includes(q)) {
            const aliasLabel = alias.replaceAll(/(^|\s)\w/g, c => c.toUpperCase());
            substringMatches.push({ ...entry, matchedAlias: aliasLabel });
            seen.add(entry.id);
        }
    }

    // Sort each group by population descending (capitals / major cities first)
    const byPopDesc = (a: City, b: City) => (b.population || 0) - (a.population || 0);
    prefixMatches.sort(byPopDesc);
    substringMatches.sort(byPopDesc);

    return deduplicateLocalResults([...prefixMatches, ...substringMatches]);
}

/**
 * Remove duplicate city-level entries from local search results.
 * When two entries are within ~15km and share the same extracted city name,
 * keep the one with richer metadata (more non-null fields like country,
 * population, POI data, spotify).
 */
function deduplicateLocalResults(cities: City[]): City[] {
    const PROXIMITY = 0.15; // ~15km
    const result: City[] = [];

    for (const entry of cities) {
        const cityLower = entry.city.toLowerCase();
        const dupIdx = result.findIndex(
            (existing) =>
                existing.city.toLowerCase() === cityLower &&
                Math.abs(existing.latitude - entry.latitude) < PROXIMITY &&
                Math.abs(existing.longitude - entry.longitude) < PROXIMITY
        );

        if (dupIdx === -1) {
            result.push(entry);
            continue;
        }
        // Keep whichever entry has richer metadata.
        const existing = result[dupIdx];
        const score = (c: City) =>
            (c.country ? 2 : 0) +
            (c.population > 0 ? 1 : 0) +
            (c.poiType ? 2 : 0) +
            (c.spotifyUri ? 1 : 0);
        const entryScore = score(entry);
        const existingScore = score(existing);
        if (entryScore > existingScore) {
            result[dupIdx] = entry;
        } else if (entryScore === existingScore) {
            // Same richness — merge missing fields into existing.
            existing.country ||= entry.country;
            existing.population ||= entry.population;
        }
    }

    return result;
}

// ─── Alias Resolution ───────────────────────────────────────────────────
// Resolves any station/city name to a canonical city_key via city_aliases,
// then returns the City object from the in-memory store.

const ALIAS_CACHE = new Map<string, string | null>();     // alias → city_key
const PENDING_ALIASES = new Map<string, Promise<string | null>>();

/**
 * Given any station or city name, resolve it to a canonical city_key.
 * Tries the raw name (lowercased), then as DB lookup in city_aliases + cities.
 * Returns null if no match found.
 */
async function lookupHyphenCityKey(raw: string): Promise<string | null> {
    if (!raw.includes("-")) return null;
    const parts = raw.split("-");
    const segments = [...new Set([parts[0], parts.at(-1) ?? ""].filter(part => part.length > 2))];
    if (!segments.length) return null;
    const { data: aliases } = await supabase.from("city_aliases").select("alias, city_key")
        .in("alias", segments).limit(1);
    if (aliases?.length) return aliases[0].city_key;
    const { data: cities } = await supabase.from("cities").select("city_key")
        .in("city_key", segments).limit(1);
    return cities?.[0]?.city_key ?? null;
}

async function lookupStationSuffixCityKey(raw: string): Promise<string | null> {
    const cleaned = extractCityName(raw).toLowerCase().trim();
    if (!cleaned || cleaned === raw) return null;
    const cleanedKey = cleaned.normalize('NFD').replaceAll(/[\u0300-\u036f]/g, '')
        .replaceAll(/[^a-z0-9]+/g, '-').replaceAll(/^-|-$/g, '');
    if (ALIAS_CACHE.has(cleanedKey)) return ALIAS_CACHE.get(cleanedKey)!;
    const { data: city } = await supabase.from("cities").select("city_key")
        .eq("city_key", cleanedKey).maybeSingle();
    if (city?.city_key) return city.city_key;
    const { data: aliases } = await supabase.from("city_aliases").select("alias, city_key")
        .in("alias", [cleaned, cleanedKey]).limit(1);
    return aliases?.[0]?.city_key ?? null;
}

async function lookupCityKey(raw: string): Promise<string | null> {
    const { data, error } = await supabase.from("city_aliases").select("alias, city_key")
        .in("alias", [raw]).limit(2);
    if (!error && data?.length) {
        for (const row of data) ALIAS_CACHE.set(row.alias, row.city_key);
        return data[0].city_key;
    }
    const { data: directMatch } = await supabase.from("cities").select("city_key")
        .eq("city_key", raw).maybeSingle();
    const result = directMatch?.city_key || await lookupHyphenCityKey(raw);
    return result || await lookupStationSuffixCityKey(raw);
}

async function resolveCityKey(name: string): Promise<string | null> {
    const raw = name.toLowerCase().trim();
    if (ALIAS_CACHE.has(raw)) return ALIAS_CACHE.get(raw)!;
    if (PENDING_ALIASES.has(raw)) return PENDING_ALIASES.get(raw)!;
    const request = (async (): Promise<string | null> => {
        try {
            const cityKey = await lookupCityKey(raw);
            ALIAS_CACHE.set(raw, cityKey);
            return cityKey;
        } catch {
            return null;
        } finally {
            PENDING_ALIASES.delete(raw);
        }
    })();
    PENDING_ALIASES.set(raw, request);
    return request;
}

/**
 * Given any station/city name, resolve to the City object from the in-memory store.
 * Uses city_aliases for name translation (e.g. "Cologne" → "koeln").
 * Returns null if the city is not in the DB or not yet loaded.
 */
export async function resolveCity(name: string): Promise<City | null> {
    const cityKey = await resolveCityKey(name);
    if (!cityKey) return null;
    return getCityById(cityKey) ?? null;
}
